/* eslint-disable react-refresh/only-export-components -- small helpers live beside their components */
// Staff invites, shared by a tenant's People tab and the Users page
// (registration plan S2): the outcome of a send, and the pending list with
// Resend, Copy link and Revoke. Links come only from the server (the send
// response's `link`, or GET /platform/invites/:id/link), never built here.

import { useState } from 'react'
import { api } from '@/api'
import { errorMessage } from '@/api/client'
import type { InviteSendResult, StaffInvite } from '@/api/types'
import {
  Badge,
  Button,
  Card,
  CardHeader,
  ConfirmDialog,
  CopyButton,
  DataTable,
  ErrorState,
  InlineAlert,
  RelativeTime,
  SkeletonRows,
  useToast,
  type Column,
} from '@/components/ui'
import { writeClipboard } from '@/components/ui/CopyButton'
import { formatDate } from '@/lib/format'
import { invalidateQueries, useQuery, type QueryKey } from '@/lib/query'

export const ROLE_LABELS: Record<string, string> = {
  admin: 'Admin',
  editor: 'Editor',
  'stat-keeper': 'Stat keeper',
  super_admin: 'Super admin',
}

/** What a stored send error means for the operator; null when the last send went out. */
export function sendErrorNote(code: string | null | undefined): string | null {
  if (!code) return null
  if (code === 'email_not_configured') return "Email isn't set up: copy the link and send it yourself."
  if (code === 'email_undeliverable') return "This address can't receive email: copy the link and send it yourself."
  if (code === 'no_platform_address') return 'This league has no ScoreLM address yet. Set its subdomain on the Domains tab.'
  return "The last email didn't go out. Resend it, or copy the link and send it yourself."
}

/** The outcome of a create or resend, with the server's link to copy when the email didn't go out. */
export function InviteResult({ result, className }: { result: InviteSendResult; className?: string }) {
  const email = result.invite.email
  if (result.delivery === 'sent') {
    return (
      <InlineAlert tone="good" className={className}>
        Invite sent to {email}.
      </InlineAlert>
    )
  }
  if (!result.link) {
    return (
      <InlineAlert tone="bad" className={className}>
        This league has no ScoreLM address yet. Set its subdomain on the Domains tab.
      </InlineAlert>
    )
  }
  return (
    <InlineAlert tone="warn" className={className} action={<CopyButton value={result.link} label="Copy link" ariaLabel={`Copy the link for ${email}`} />}>
      {result.delivery === 'not_configured'
        ? "Email isn't set up on this server: copy the link and send it yourself."
        : result.delivery === 'undeliverable'
          ? "This address can't receive email: copy the link and send it yourself."
          : "The email didn't go out. Copy the link and send it yourself."}
    </InlineAlert>
  )
}

function isExpired(i: StaffInvite): boolean {
  return i.status === 'expired' || Date.parse(i.expires_at) <= Date.now()
}

/** Pending invites with Resend, Copy link and Revoke. Hidden when there are none. */
export function PendingInvitesCard({
  title,
  queryKey,
  load,
  showRole = false,
}: {
  title: string
  queryKey: QueryKey
  load: (signal: AbortSignal) => Promise<StaffInvite[]>
  showRole?: boolean
}) {
  const toast = useToast()
  const { data, error, status, refetch } = useQuery(queryKey, load)
  const [resending, setResending] = useState<StaffInvite | null>(null)
  const [revoking, setRevoking] = useState<StaffInvite | null>(null)
  const [reinviting, setReinviting] = useState<StaffInvite | null>(null)
  const [results, setResults] = useState<Record<number, InviteSendResult>>({})
  const [manual, setManual] = useState<Record<number, string>>({})
  const reload = () => invalidateQueries(String(queryKey[0]))

  const copy = async (i: StaffInvite) => {
    try {
      const { url } = await api.invites.inviteLink(i.id)
      if (await writeClipboard(url)) {
        toast.success('Link copied')
        setManual((m) => {
          const next = { ...m }
          delete next[i.id]
          return next
        })
      } else setManual((m) => ({ ...m, [i.id]: url }))
    } catch (err) {
      toast.error(errorMessage(err))
      reload()
    }
  }

  const afterSend = (i: StaffInvite, r: InviteSendResult) => {
    if (r.delivery === 'sent') {
      toast.success(`Invite sent to ${i.email}.`)
      setResults((m) => {
        const next = { ...m }
        delete next[i.id]
        return next
      })
    } else setResults((m) => ({ ...m, [r.invite.id]: r }))
  }

  if (status === 'loading' && !data) return <SkeletonRows rows={2} label={`Loading ${title.toLowerCase()}`} />
  if (status === 'error' && !data) return <ErrorState error={error} title={`Couldn't load ${title.toLowerCase()}`} onRetry={refetch} />
  const invites = data ?? []
  if (invites.length === 0) return null

  const columns: Column<StaffInvite>[] = [
    {
      id: 'email',
      header: 'Email',
      primary: true,
      cell: (i) => {
        const note = sendErrorNote(i.last_send_error)
        return (
          <span className="grid min-w-0 gap-0.5">
            <span className="break-words font-semibold text-ink [overflow-wrap:anywhere]">{i.email}</span>
            {note ? <span className="text-xs text-warn">{note}</span> : null}
            {results[i.id] ? <InviteResult result={results[i.id]} className="mt-1" /> : null}
            {manual[i.id] ? (
              <span className="mt-1 flex min-w-0 items-center gap-2">
                <span className="min-w-0 break-all font-mono text-xs text-muted">{manual[i.id]}</span>
                <CopyButton value={manual[i.id]} ariaLabel={`Copy the shown link for ${i.email}`} />
              </span>
            ) : null}
          </span>
        )
      },
    },
    ...(showRole ? [{ id: 'role', header: 'Role', cell: (i: StaffInvite) => ROLE_LABELS[i.role] ?? i.role }] : []),
    {
      id: 'sent',
      header: 'Sent',
      cell: (i) => (
        <span className="whitespace-nowrap text-muted">
          <RelativeTime value={i.last_sent_at ?? i.created_at} /> · {i.send_count === 1 ? '1 send' : `${i.send_count} sends`}
        </span>
      ),
    },
    {
      id: 'expires',
      header: 'Expires',
      cell: (i) => (isExpired(i) ? <Badge tone="bad">Expired</Badge> : <span className="whitespace-nowrap tabular text-muted">{formatDate(i.expires_at)}</span>),
    },
    {
      id: 'actions',
      header: <span className="sr-only">Actions</span>,
      mobileLabel: ' ',
      align: 'right',
      cell: (i) => (
        <span className="flex flex-wrap justify-end gap-1.5">
          {isExpired(i) ? (
            <Button size="sm" onClick={() => setReinviting(i)} aria-label={`Invite ${i.email} again`}>
              Invite again
            </Button>
          ) : (
            <>
              <Button size="sm" onClick={() => setResending(i)} disabled={i.send_count >= 10} title={i.send_count >= 10 ? 'Sent 10 times. Revoke it and send a new one.' : undefined} aria-label={`Resend invite to ${i.email}`}>
                Resend
              </Button>
              <Button size="sm" onClick={() => void copy(i)} aria-label={`Copy invite link for ${i.email}`}>
                Copy link
              </Button>
            </>
          )}
          <Button size="sm" variant="ghost" onClick={() => setRevoking(i)} aria-label={`Revoke invite to ${i.email}`}>
            Revoke
          </Button>
        </span>
      ),
    },
  ]

  return (
    <Card flush className="overflow-hidden">
      <div className="px-4 pt-4 sm:px-5">
        <CardHeader title={`${title} (${invites.length})`} description="Sent but not yet accepted." />
      </div>
      <div className="border-t border-line">
        <DataTable caption={title} rows={invites} columns={columns} rowKey={(i) => i.id} />
      </div>

      <ConfirmDialog
        open={!!resending}
        onClose={() => setResending(null)}
        tone="primary"
        title={`Resend the invite to ${resending?.email ?? ''}?`}
        description="They get the same link again. It still expires when it was going to."
        confirmLabel="Resend invite"
        onConfirm={async () => {
          const i = resending!
          try {
            afterSend(i, await api.invites.resendInvite(i.id))
          } finally {
            reload()
          }
        }}
      />
      <ConfirmDialog
        open={!!revoking}
        onClose={() => setRevoking(null)}
        title={`Revoke the invite to ${revoking?.email ?? ''}?`}
        description="The link stops working. You can invite them again."
        confirmLabel="Revoke invite"
        onConfirm={async () => {
          const i = revoking!
          try {
            await api.invites.revokeInvite(i.id)
            toast.success(`Revoked the invite to ${i.email}.`)
          } finally {
            reload()
          }
        }}
      />
      <ConfirmDialog
        open={!!reinviting}
        onClose={() => setReinviting(null)}
        tone="primary"
        title={`Invite ${reinviting?.email ?? ''} again?`}
        description="They get a new email with a new link."
        confirmLabel="Send invite"
        onConfirm={async () => {
          const i = reinviting!
          try {
            const r =
              i.league_id === null
                ? await api.invites.inviteSuperAdmin(i.email)
                : await api.invites.inviteToTenant(i.league_id, { email: i.email, role: i.role === 'super_admin' ? 'admin' : i.role })
            afterSend(i, r)
          } finally {
            reload()
          }
        }}
      />
    </Card>
  )
}
